import axios from "axios";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import { BASE_URL } from "../constants/constants.js";
import { UserContext } from "../contexts/UserContext";
import TokenService from "../../services/token.service.js";

export default function useAuth() {
  const navigate = useNavigate();

  const { setUser } = useContext(UserContext);
  const [error, setError] = useState(null);

  const parseJwt = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  };

  const setUserContext = async (token) => {
    const payload = parseJwt(token);
    const uuid = payload?.user_id || payload?.uuid;

    if (!uuid) {
      setError("UUID not found in token");
      return;
    }

    localStorage.setItem("uuid", uuid);

    try {
      const response = await axios.get(`${BASE_URL}/auth/users/${uuid}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUser(response.data);

      if (response.data.roles.some((role) => role.code === "STD")) {
        navigate({
          pathname: `/portfolio`,
          state: { user: response.data },
        });
      } else if (response.data.roles.some((role) => role.code === "EXP")) {
        navigate({ pathname: "/expert/reviews" });
      } else if (response.data.roles.some((role) => role.code === "UNI")) {
        navigate({ pathname: "/organization/dashboard" });
      } else if (response.data.roles.some((role) => role?.code === "ADMIN")) {
        navigate({ pathname: `/universities`, state: { user: response.data } });
      } else {
        navigate({ pathname: `/profile`, state: { user: response.data } });
      }

      return response.data;
    } catch (err) {
      setError(err.response?.data);
      throw err;
    }
  };

  const loginUser = async (data) => {
    return axios
      .post(BASE_URL + "/auth/login/", data)
      .then(async (response) => {
        TokenService.updateLocalAccessToken(response.data.access);
        API.defaults.headers["Authorization"] = `Bearer ${response.data.access}`;
        TokenService.updateLocalRefreshToken(response.data.refresh);
        await setUserContext(response.data.access);
      })
      .catch((err) => {
        setError(err.response?.data || "Something went wrong.");
        throw err;
      });
  };

  const registerUser = async (data) => {
    const formattedData = {
      email: data.email,
      password: data.password,
      first_name: data.first_name || "",
      middle_name: data.middle_name || null,
      last_name: data.last_name || "",
      date_of_birth: data.date_of_birth,
    };

    try {
      const response = await axios.post(
        `${BASE_URL}/auth/registration/`,
        formattedData
      );
      return response.data;
    } catch (err) {
      throw err.response?.data || err;
    }
  };

  const resetPassword = async (tokenId, newPassword) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/auth/request_password_reset/${tokenId}/`,
        {
          password: newPassword,
        }
      );
      return {
        success: true,
        message: response.data?.message || "Password updated!",
      };
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Reset failed or token expired.";
      return { success: false, message: errorMsg };
    }
  };

  const verifyEmailToken = async (token) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/auth/verify_email/${token}/`
      );
      return { success: true, message: response.data?.message || "Verified!" };
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Verification failed or token expired.";
      setError(errorMsg);
      return { success: false, message: errorMsg };
    }
  };

  const passwordResetToken = async (email) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/auth/request_password_reset/`,
        { email: email }
      );
      return {
        success: true,
        message: response.data?.message || "Email sent!",
      };
    } catch (err) {
      const errorMsg =
        err.response?.data?.error || "Failed to send reset email.";
      return { success: false, message: errorMsg };
    }
  };

  return {
    loginUser,
    registerUser,
    setUserContext,
    verifyEmailToken,
    passwordResetToken,
    resetPassword,
    error,
  };
}
